import * as React from "react";
import * as _ from "lodash";
import {connect} from "react-redux";
import * as moment from "moment";
import {LinkContainer} from "react-router-bootstrap";
import {NavItem} from "react-bootstrap";
import {IAppointmentScheduleResponse, IPatientDetail, IUnreadMessageInfo} from "@curefit/care-common";
import {AppointmentsActions} from "../actions/AppointmentsActions";
import PatientAppointmentCard from "../components/patient/PatientAppointmentCard";
import {IAppState} from "../models/IAppState"
import IDoctorProfile from "../models/IDoctorProfile"
import AppLoader from "../components/utils/AppLoader";
import { AppMessages, ConsultationStatusConstant, ORION_IMG_URL } from "../utils/AppConstants";
import {OngoingConsultationActions} from "../actions/OngoingConsultationActions";
import {IAccessToken} from "../models/ICommunication";
import {ITwilioContext} from "../models/ITwilioContext";
import {default as AppUtils, LOADING_STATE} from "../utils/AppUtils";
import ModelFactory from "../models/ModelFactory";
import LazyLoaderComponent from "../components/LazyLoaderComponent";
import {TwilioActions} from "../actions/TwilioActions";
import {PatientCompleteInfoActions} from "../actions/PatientCompleteInfoActions";
import DoctorPatientCard from "../components/patient/DoctorPatientCard";
import SearchForm from "../components/common/SearchForm";
import {AppointmentTypeFlagMap} from "../models/appointment/AppointmentTypeFlag";
import AlbusApis from "../api/AlbusApis";
import {ApiManager} from "../api/ApiManager";
import {EventNames} from "../utils/EventTrackerConstants";

export interface MyPatientsPageProps {
    doctorProfile: IDoctorProfile,
    chatAccessToken: IAccessToken[],
    twilioContext: ITwilioContext,
    fetchPaginatedDoctorAppointments: (doctorId: number, pageNo: number, pageSize: number, appointmentTypeFlags?: AppointmentTypeFlagMap, fromTimeEpoch?: number) => Promise<IAppointmentScheduleResponse[]>,
    fetchChatContext: (appointmentId: number, commUserId: number) => null,
    updateCurrentChannelWithPatient: (channelSid: string, patient: IPatientDetail, identity: string) => void,
    fetchDoctorPatients: (doctorId: any, query: any) => Promise<any>
}

export interface DoctorPatient {
    doctorId: number;
    patientDetail: IPatientDetail;
    lastCompletedConsultation?: number;
    twilioModeId: string;
    lastChatTime?: number;
    hasUpcoming: boolean;
    hasInDraft: boolean;
    isChatExpired?: boolean;
    expiry: number;
    doctorMemberIdentity: string
}

export interface DoctorPatientsSearchResponse {
    content: DoctorPatient[];
    last: boolean;
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    sort?: any;
    first: boolean;
    numberOfElements: number;
}

export interface MyPatientsPageState {
    upcomingConsultations: IAppointmentScheduleResponse[],
    endOfUpcomingConsultations: boolean,
    completedConsultations: IAppointmentScheduleResponse[],
    endOfCompletedConsultations: boolean,
    draftConsultations: IAppointmentScheduleResponse[],
    endOfDraftConsultations: boolean,
    selectedTabIdx: string,
    chatChannelName: string,
    activeAppointment: number,
    channelSid: string,
    activePatientName: string,
    currentCompletedConsultationsPage: number,
    currentUpcomingConsultationsPage: number,
    appointmentsLoadingState: LOADING_STATE,
    currentOngoingConsultationsPage: number,
    fromTimeEpoch: number,
    completedConsultationsCount: number,
    upcomingConsultationsCount: number,
    ongoingConsultationsCount: number
}

export const MyPatientsPageSize = 15;
export const DoctorPatientsPageSize = 25;

class MyPatientsPage extends React.Component<MyPatientsPageProps, MyPatientsPageState> {
    private loadStartTime: number = AppUtils.getCurrentTime()
    constructor(props: MyPatientsPageProps) {
        super(props);
        this.state = {
            upcomingConsultations: null,
            endOfUpcomingConsultations: false,
            completedConsultations: null,
            endOfCompletedConsultations: false,
            draftConsultations: null,
            endOfDraftConsultations: false,
            selectedTabIdx: "1",
            chatChannelName: null,
            activeAppointment: null,
            channelSid: null,
            activePatientName: null,
            currentCompletedConsultationsPage: 0,
            currentUpcomingConsultationsPage: 0,
            currentOngoingConsultationsPage: 0,
            appointmentsLoadingState: LOADING_STATE.NOT_INITIALIZED,
            fromTimeEpoch: moment().subtract(14, "day").valueOf(),
            completedConsultationsCount: 0,
            upcomingConsultationsCount: 0,
            ongoingConsultationsCount: 0
        };
    };

    componentWillMount() {
        const {fromTimeEpoch} = this.state
        this.props.fetchPaginatedDoctorAppointments(this.props.doctorProfile.id, 0, MyPatientsPageSize, {showUpcomingConsultation: true}, fromTimeEpoch)
            .then((appointments: IAppointmentScheduleResponse[]) => {
                AppUtils.updatePageLoadEvent(EventNames.My_Consultations, EventNames.Upcoming, this.loadStartTime)
                this.setState({
                    upcomingConsultations: appointments
                })
            })
        this.props.fetchPaginatedDoctorAppointments(this.props.doctorProfile.id, 0, MyPatientsPageSize, {showCompletedConsultationInMovingWindow: true}, fromTimeEpoch)
            .then((appointments: IAppointmentScheduleResponse[]) => {
                AppUtils.updatePageLoadEvent(EventNames.My_Consultations, EventNames.Complete, this.loadStartTime)
                this.setState({
                    completedConsultations: appointments
                })
            })
        this.props.fetchPaginatedDoctorAppointments(this.props.doctorProfile.id, 0, MyPatientsPageSize, {showOngoingConsultationInMovingWindow: true}, fromTimeEpoch)
            .then((appointments: IAppointmentScheduleResponse[]) => {
                AppUtils.updatePageLoadEvent(EventNames.My_Consultations, EventNames.Drafts, this.loadStartTime)
                this.setState({
                    draftConsultations: appointments
                })
            })
        ApiManager.getAlbusApis().getConsultationsCount(this.props.doctorProfile.id, this.state.fromTimeEpoch).then((response: any) => {
            this.setState({
                completedConsultationsCount: response["COMPLETED"] || 0,
                upcomingConsultationsCount: response["UPCOMING"] || 0,
                ongoingConsultationsCount: response["ONGOING"] || 0
            })
        }).catch((error: any) => {
            console.error(`Error while fetching consultations count: ${JSON.stringify(error)}`)
        })
    }

    handleTabClick(selection: any) {
        this.setState({
            selectedTabIdx: selection
        });
    }

    /*setChatContext = (appointmentId: number, channelSid: string, patientName: string, context: IAppointmentActionsWithContext) => {
        if (!this.props.chatAccessToken) this.props.fetchChatContext(appointmentId, this.props.doctorProfile.id);
        this.setState({
            activeAppointment: appointmentId,
            channelSid,
            chatChannelName: context.chatActionWithContext.context.twilioCommunicationMode.modeName,
            activePatientName: patientName
        })
    }*/

    setChatContext = (channelSid: string, patient: IPatientDetail, identity: string) => {
        this.props.updateCurrentChannelWithPatient(channelSid, patient, identity);
    }

    getSortedConsultations = (consultationsWithoutUnreadCount: IAppointmentScheduleResponse[]) => {
        let channelInfo = this.props.twilioContext.channelInfo;
        let consultations: IAppointmentScheduleResponse[] = [];
        _.map(consultationsWithoutUnreadCount, (consultation) => {
            if (!_.isEmpty(consultation.appointmentActionsWithContext.chatActionWithContext.context)) {
                let sid = consultation.appointmentActionsWithContext.chatActionWithContext.context.twilioCommunicationMode.modeSid;
                let unreadCount = !_.isNull(channelInfo) && channelInfo.filter(item => item.id === sid).length ? channelInfo.filter(item => item.id === sid)[0].unreadCount : 0;
                let unreadMessageInfo: IUnreadMessageInfo = ModelFactory.createNewUnreadMessageInfo();
                unreadMessageInfo.unreadMessageCount = unreadCount || 0;
                consultation.appointmentActionsWithContext.chatActionWithContext.context.unreadMessageInfo = unreadMessageInfo;
                consultations.push(consultation);
            }
        })
        let unreadConsultations = consultations.filter(consultation => consultation.appointmentActionsWithContext.chatActionWithContext.context.unreadMessageInfo.unreadMessageCount > 0);
        let readConsultations = consultations.filter(consultation => consultation.appointmentActionsWithContext.chatActionWithContext.context.unreadMessageInfo.unreadMessageCount === 0);
        unreadConsultations.sort((x, y) => { return y.startTime - x.startTime })
        readConsultations.sort((x, y) => { return y.startTime - x.startTime })
        return unreadConsultations.concat(readConsultations);
    }

    loadMoreConsultations = () => {
        const {fromTimeEpoch} = this.state
        if (this.state.selectedTabIdx === "1" && !this.state.endOfCompletedConsultations && this.state.appointmentsLoadingState !== LOADING_STATE.LOADING) {
            let nextPageNo = this.state.currentCompletedConsultationsPage + 1;
            this.setState({
                appointmentsLoadingState: LOADING_STATE.LOADING
            }, () => {
                this.props.fetchPaginatedDoctorAppointments(this.props.doctorProfile.id, nextPageNo, MyPatientsPageSize, {showCompletedConsultationInMovingWindow: true}, fromTimeEpoch)
                    .then((appointments: IAppointmentScheduleResponse[]) => {
                        if (!_.isEmpty(appointments)) {
                            let completedAppointments = this.state.completedConsultations;
                            this.setState({
                                currentCompletedConsultationsPage: nextPageNo,
                                completedConsultations: completedAppointments.concat(appointments),
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        } else {
                            this.setState({
                                endOfCompletedConsultations: true,
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        }
                    })
            })
        }
        else if (this.state.selectedTabIdx === "2" && !this.state.endOfUpcomingConsultations && this.state.appointmentsLoadingState !== LOADING_STATE.LOADING) {
            let nextPageNo = this.state.currentUpcomingConsultationsPage + 1;
            this.setState({
                appointmentsLoadingState: LOADING_STATE.LOADING
            }, () => {
                this.props.fetchPaginatedDoctorAppointments(this.props.doctorProfile.id, nextPageNo, MyPatientsPageSize, {showUpcomingConsultation: true}, fromTimeEpoch)
                    .then((appointments: IAppointmentScheduleResponse[]) => {
                        if (!_.isEmpty(appointments)) {
                            let upcomingAppointments = this.state.upcomingConsultations;
                            this.setState({
                                currentUpcomingConsultationsPage: nextPageNo,
                                upcomingConsultations: upcomingAppointments.concat(appointments),
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        } else {
                            this.setState({
                                endOfUpcomingConsultations: true,
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        }
                    })
            })
        }
        else if (this.state.selectedTabIdx === "3" && !this.state.endOfDraftConsultations && this.state.appointmentsLoadingState !== LOADING_STATE.LOADING) {
            let nextPageNo = this.state.currentOngoingConsultationsPage + 1;
            this.setState({
                appointmentsLoadingState: LOADING_STATE.LOADING
            }, () => {
                this.props.fetchPaginatedDoctorAppointments(this.props.doctorProfile.id, nextPageNo, MyPatientsPageSize, {showOngoingConsultationInMovingWindow: true}, fromTimeEpoch)
                    .then((appointments: IAppointmentScheduleResponse[]) => {
                        if (!_.isEmpty(appointments)) {
                            let draftConsultations = this.state.draftConsultations;
                            this.setState({
                                currentOngoingConsultationsPage: nextPageNo,
                                draftConsultations: draftConsultations.concat(appointments),
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        } else {
                            this.setState({
                                endOfDraftConsultations: true,
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        }
                    })
            })
        }
    }

    findUnreadCountForPatient = (patientId: number): number => {
        let unreadMessageInfoList = this.props.twilioContext.unreadMessageInfo;
        let index = (unreadMessageInfoList && unreadMessageInfoList.map(item => item.patient.id).indexOf(patientId)) || -1;
        let unreadCount = null;
        if (index !== -1) unreadCount = unreadMessageInfoList[index].twilioUnreadMessageCountView.unreadMessageCount;
        return unreadCount;
    }

    getSelectedConsultations = () => {
        if (this.state.selectedTabIdx === "1") {
            return this.state.completedConsultations;
        } else if (this.state.selectedTabIdx === "2") {
            return this.state.upcomingConsultations;
        } else {
            return this.state.draftConsultations;
        }
    }

    isEndOfList = () => {
        let flag = false
        switch (this.state.selectedTabIdx) {
            case "1":
                flag = this.state.endOfCompletedConsultations
                break;
            case "2":
                flag = this.state.endOfUpcomingConsultations
                break;
            case "3":
                flag = this.state.endOfDraftConsultations
                break;
        }
        return flag
    }

    render() {
        let myInstance = this;
        let completedTabCssClasses = "tab " + (myInstance.state.selectedTabIdx === "1" ? "active" : "");
        let upcomingTabCssClasses = "tab " + (myInstance.state.selectedTabIdx === "2" ? "active" : "");
        let draftTabCssClasses = "tab " + (myInstance.state.selectedTabIdx === "3" ? "active" : "");
        let endOfList = this.isEndOfList()
        let selectedTabConsultations = this.getSelectedConsultations();
        return (
            <div className="my-patients-page">
                <div className="dummy-background-div"></div>
                {
                    !_.isNull(selectedTabConsultations) ?
                    <div>
                        <div className="tabs-container">
                            <div className={completedTabCssClasses} onClick={() => myInstance.handleTabClick("1")}>
                                <label>
                                    <label className="label-text brandon-bold">Completed</label>
                                    <label className="count brandon-bold">{this.state.completedConsultationsCount > 0 ? this.state.completedConsultationsCount : ""}</label>
                                </label>
                            </div>
                            <div className={upcomingTabCssClasses} onClick={() => myInstance.handleTabClick("2")}>
                                <label>
                                    <label className="label-text brandon-bold">Upcoming</label>
                                    <label className="count brandon-bold">{this.state.upcomingConsultationsCount > 0 ? this.state.upcomingConsultationsCount : ""}</label>
                                </label>
                            </div>
                            <div className={draftTabCssClasses} onClick={() => myInstance.handleTabClick("3")}>
                                <label>
                                    <label className="label-text brandon-bold">Ongoing</label>
                                    <label className="count brandon-bold">{this.state.ongoingConsultationsCount > 0 ? this.state.ongoingConsultationsCount : ""}</label>
                                </label>
                            </div>
                        </div>
                        {
                            selectedTabConsultations !== null ?
                                <div className="consultations-table my-patients-page-table">
                                    <div className="consultation-header">
                                        <div className="brandon-bold">Name</div>
                                        <div className="brandon-bold">Consultation</div>
                                        <div className="brandon-bold">Chat</div>
                                    </div>
                                    <LazyLoaderComponent reachedEndTrigger={this.loadMoreConsultations}>
                                        <div className="consultation-body">
                                            {
                                                _.map(selectedTabConsultations, (consultation: IAppointmentScheduleResponse, index) => {
                                                    let isActive = consultation.id === myInstance.state.activeAppointment;
                                                    let consultationUrl = AppUtils.getConsultationUrl(consultation.patient.id, consultation.id);
                                                    if (myInstance.state.selectedTabIdx === "3") consultationUrl = AppUtils.getOngoingConsultationUrl(consultation.patient.id, consultation.id);
                                                    let unreadCount = this.findUnreadCountForPatient(consultation.patient.id);
                                                    return (
                                                        <LinkContainer to={consultationUrl} key={`my-patients-${consultation.id}`}>
                                                            <NavItem title="Completed">
                                                                <PatientAppointmentCard appointment={consultation} key={consultation.id} openChat={myInstance.setChatContext} isActive={isActive} unreadCount={unreadCount} />
                                                            </NavItem>
                                                        </LinkContainer>
                                                    )
                                                } )
                                            }
                                        </div>
                                    </LazyLoaderComponent>
                                    {
                                        this.state.appointmentsLoadingState === LOADING_STATE.LOADING &&
                                        <div className={"loading-appointments"}>
                                        <span>
                                            <img src={ORION_IMG_URL + "loader/rolling-loader.gif"} />
                                        </span>
                                        </div>
                                    }
                                    {
                                        endOfList &&
                                        <div className={"end-of-consultations-list"}>
                                            <div style={{marginTop: "-10px"}}><span>{AppMessages.MY_PATIENTS_PAGE_END_OF_RESULTS}</span></div>
                                        </div>
                                    }
                                    </div> : <div className="central-loader my-patients-loader">
                                        <img src={ORION_IMG_URL + "loader/curefit-loader.gif"} />
                                    </div>
                        }
                        </div> : <div className="central-loader my-patients-loader">
                            <img src={ORION_IMG_URL + "loader/curefit-loader.gif"} />
                        </div>
                }
            </div>
        )
    }
}

function mapStateToProps(state: IAppState): any {
    return {
        doctorProfile: state.doctorProfile,
        chatAccessToken: state.chatAndVideoAccessTokens.chatAccessToken,
        twilioContext: state.twilioContext
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        updateCurrentChannelWithPatient: (channelSid: string, patient: IPatientDetail, identity: string) => {
            return dispatch(TwilioActions.updateCurrentChannelWithPatient(channelSid, patient, identity));
        },
        fetchChatContext: (appointmentId: number, commUserId: number) => {
            OngoingConsultationActions.fetchChatContext(dispatch, appointmentId, commUserId);
        },
        fetchPaginatedDoctorAppointments: (doctorId: number, pageNo: number, pageSize: number, appointmentTypeFlags?: AppointmentTypeFlagMap, fromTimeEpoch?: number) => {
            return AppointmentsActions.fetchPaginatedDoctorAppointments(dispatch, doctorId, pageNo, pageSize, appointmentTypeFlags, fromTimeEpoch)
        },
        fetchDoctorPatients: (doctorId: number, query: any) => {
            return PatientCompleteInfoActions.searchDoctorPatients(doctorId, query)
        }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(MyPatientsPage);